import { ReactElement, useEffect } from 'react';
import { type DefaultValues, type FieldValues, FormProvider, type Mode, type Resolver, type SubmitHandler, useForm, type UseFormReturn } from 'react-hook-form';
import { FormCheckbox } from 'softcat:components/form-checkbox.tsx';
import { FormFileUpload } from 'softcat:components/form-file-upload.tsx';
import { FormFooter } from 'softcat:components/form-footer.tsx';
import { FormHeader } from 'softcat:components/form-header.tsx';
import { FormInput } from 'softcat:components/form-input.tsx';
import { FormLabel } from 'softcat:components/form-label.tsx';
import { FormListbox } from 'softcat:components/form-listbox.tsx';
import { FormRadioGroup } from 'softcat:components/form-radio-group.tsx';
import { FormRemoteSearchCombobox } from 'softcat:components/form-remote-search-combobox.tsx';
import { FormSortableList } from 'softcat:components/form-sortable-list.tsx';
import { FormStaticBadges } from 'softcat:components/form-static-badges.tsx';
import { FormStaticValue } from 'softcat:components/form-static-value.tsx';
import { FormTextarea } from 'softcat:components/form-textarea.tsx';

export interface FormProperties<TFormType extends FieldValues = FieldValues> {
    children: ReactElement | ReactElement[];
    customForm?: UseFormReturn<TFormType>;
    defaultValues?: DefaultValues<TFormType>;
    mode?: Mode;
    onSubmit: SubmitHandler<TFormType>;
    resolver?: Resolver<TFormType>;
}

export const Form = <TFormType extends FieldValues>({
    children,
    defaultValues,
    customForm,
    mode = 'onSubmit',
    onSubmit,
    resolver,
}: FormProperties<TFormType>) => {
    const form = useForm<TFormType>({ defaultValues, mode, resolver });
    const methods = customForm || form;

    useEffect(() => {
        if (defaultValues && !customForm) {
            methods.reset(defaultValues);
        }
    }, [defaultValues, methods, customForm]);

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>{children}</form>
        </FormProvider>
    );
};

Form.Checkbox = FormCheckbox;
Form.FileUpload = FormFileUpload;
Form.Footer = FormFooter;
Form.Header = FormHeader;
Form.Input = FormInput;
Form.Label = FormLabel;
Form.Listbox = FormListbox;
Form.RadioGroup = FormRadioGroup;
Form.RemoteSearchCombobox = FormRemoteSearchCombobox;
Form.SortableList = FormSortableList;
Form.StaticBadges = FormStaticBadges;
Form.StaticValue = FormStaticValue;
Form.Textarea = FormTextarea;
